import * as React from 'react'
import { IncomingSelfBillingOrdersController } from '../../controllers'
import Notification from '../../utilities/Notification'
import CardEmptyInfo from '../Card/CardEmptyInfo'
import { withRouter, RouteComponentProps } from 'react-router'
import RouteHelper from '../../helpers/RouteHelper'
import ERoute from '../../ERoute';
import PageLoader from '../Page/PageLoader'
import { AnyAction, Dispatch } from 'redux'
import { showConfirmModal, showIncomingSelfBillingOrderModal } from '../../store/modals/actions'
import { connect } from 'react-redux'
import LocalStorage, { LocalStorageKey } from '../../LocalStorage'
import { Helmet } from 'react-helmet'
import { WithTranslation, withTranslation } from 'react-i18next'
import ResourceTable from '../Resource/ResourceTable'
import { CurrentUser, IncomingSelfBillingOrderFilter, IncomingSelfBillingOrder, IncomingSelfBillingOrderFilters, ResourceListFilterType } from '../../types'
import { AppState } from '../../store'
import SearchHelper from '../../helpers/SearchHelper'
import NavigationDetection from '../Effects/NavigationDetection'
import IncomingSelfBillingOrderHelper from '../../helpers/IncomingSelfBillingOrderHelper'
import getSelfBillingOrderTableRenderer from '../Resource/SelfBillingOrderRenderer'

export interface IComponentProps {
  contactId?: string
  onItemUpdated?: () => void,
  onItemDeleted?: () => void,
  urlRewriteEnabled?: boolean
}

interface IStateToProps {
  currentUser: CurrentUser
}
interface IDispatchToProps {
  showConfirmModal: typeof showConfirmModal
  showIncomingSelfBillingOrderModal: typeof showIncomingSelfBillingOrderModal
}

type IProps = IComponentProps & IStateToProps & IDispatchToProps & RouteComponentProps<any> & WithTranslation

interface IState {
  orders: IncomingSelfBillingOrder[]
  page: number
  perPage: number
  totalPages: number
  didInitialLoad: boolean
  sortValue: string
  isFetching: boolean
  activeFilters: any
  search: string
}

class IncomingSelfBillingOrdersTable extends React.Component<IProps, IState> {
  constructor(props) {
    super(props)

    const { search, page, perPage, filters, } = SearchHelper.getSearch(this.props.location.search, IncomingSelfBillingOrderFilters)

    this.state = {
      orders: [],
      page: page,
      perPage: perPage,
      totalPages: 1,
      didInitialLoad: false,
      sortValue: LocalStorage.get(LocalStorageKey.INCOMING_SELF_BILLING_ORDER_SORT_VALUE, 'number_desc'),
      isFetching: false,
      search: search,
      activeFilters: filters,
    }

    this.onRouteChange = this.onRouteChange.bind(this)
    this.onTableClick = this.onTableClick.bind(this);
    this.onTableDeleteClick = this.onTableDeleteClick.bind(this);
    this.onFiltersChange = this.onFiltersChange.bind(this)
    this.onSortValueChange = this.onSortValueChange.bind(this)
    this.onPerPageChange = this.onPerPageChange.bind(this)
    this.onSearchChange = this.onSearchChange.bind(this)
    this.onSearchSubmit = this.onSearchSubmit.bind(this)
    this.onClearFilters = this.onClearFilters.bind(this)
    this.onPageChange = this.onPageChange.bind(this)
    this.onSelfBillingOrderColumnsChange = this.onSelfBillingOrderColumnsChange.bind(this)
    this.onTableActionClick = this.onTableActionClick.bind(this)
  }

  onRouteChange() {
    const { search, page, filters } = SearchHelper.getSearch(this.props.location.search, IncomingSelfBillingOrderFilters)
    this.setState({ search: search, page: page, activeFilters: filters }, () => {
      this.fetchIncomingSelfBillingOrders(page)
    })
  }

  rehydratedFetch() {
    const { urlRewriteEnabled } = this.props
    const { page, activeFilters, search, sortValue } = this.state

    if (urlRewriteEnabled) {
      RouteHelper.replace(this.props.location.pathname as ERoute, { page: page, search: search, ...activeFilters, sortValue: sortValue })
    } else {
      this.fetchIncomingSelfBillingOrders(page)
    }
  }

  fetchIncomingSelfBillingOrders(page = 1) {
    const { search, sortValue, activeFilters, perPage } = this.state
    const { contactId } = this.props
    let parameters: any = {
      search: search,
      order: sortValue,
      page: page,
      per_page: perPage,
      ...activeFilters,
    };

    if (contactId) {
      parameters = {
        ...parameters,
        contact_id: contactId,
      }
    }

    this.setState({ isFetching: true })
    IncomingSelfBillingOrdersController
      .getIncomingSelfBillingOrders(parameters)
      .then(response => {
        const { current_page, total_pages, incoming_self_billing_orders } = response

        this.setState({
          orders: incoming_self_billing_orders,
          page: current_page,
          totalPages: total_pages,
          didInitialLoad: true,
          isFetching: false,
        });
      })
      .catch(console.error)
      .finally(() => {
        this.setState({ isFetching: false })
      })
  }

  deleteIncomingSelfBillingOrder(order, callback) {
    const { t } = this.props

    IncomingSelfBillingOrdersController
      .delete(order.id)
      .then(response => {
        Notification.notifySuccess(t('IncomingSelfBillingOrdersTable::Deleted successfully'))
        callback(response);
      })
  }

  onTableClick(order: IncomingSelfBillingOrder) {
    this.props.showIncomingSelfBillingOrderModal({
      incomingSelfBillingOrder: {
        id: order.id,
      },
      onSubmit: (incomingSelfBillingOrder) => {
        const { orders } = this.state

        const orderIndex = orders.findIndex(i => i.id === incomingSelfBillingOrder.id);

        orders[orderIndex] = incomingSelfBillingOrder

        this.setState({ orders: [...orders] })
      }
    })
  }

  onTableDeleteClick(order: IncomingSelfBillingOrder) {
    const { onItemDeleted, showConfirmModal, t } = this.props

    const typeLabel = IncomingSelfBillingOrderHelper.getTypeLabel(order.type)

    showConfirmModal({
      title: t('IncomingSelfBillingOrdersTable::Delete {{type}}', { type: typeLabel }),
      description: t('IncomingSelfBillingOrdersTable::You are about to delete a {{type}}. Deletion is permanent and irreversible. Are you sure?', { type: typeLabel.toLowerCase() }),
      action: { label: t('IncomingSelfBillingOrdersTable::Delete'), isDestructive: true },
      onConfirm: () => {
        this.deleteIncomingSelfBillingOrder(order, () => {
          const { orders } = this.state

          const orderIndex = orders.findIndex(i => i.id === order.id);
          orders.splice(orderIndex, 1);

          this.setState({ orders });

          if (onItemDeleted) onItemDeleted()
        })
      }
    })
  }

  onTableActionClick(key: string, order: IncomingSelfBillingOrder) {
    switch (key) {
      case 'view': this.onTableClick(order)
        break
      case 'delete': this.onTableDeleteClick(order)
        break
      default:
        throw Error('[IncomingSelfBillingOrdersTable] Unimplemented onTableActionClick')
    }
  }

  onFiltersChange(activeFilters: any) {
    this.setState({ page: 1, activeFilters: activeFilters }, () => {
      this.rehydratedFetch()
    })
  }

  onSortValueChange(value: string) {
    LocalStorage.set(LocalStorageKey.INCOMING_SELF_BILLING_ORDER_SORT_VALUE, value)

    this.setState({ page: 1, sortValue: value, }, () => {
      this.rehydratedFetch()
    })
  }

  onSearchChange(search) {
    this.setState({ search: search })
  }

  onSearchSubmit(search) {
    this.setState({ page: 1, search: search }, () => {
      this.rehydratedFetch()
    })
  }

  onClearFilters() {
    this.setState({
      page: 1,
      search: '',
      activeFilters: {}
    }, this.rehydratedFetch)
  }

  onPageChange(page) {
    this.setState({ page: page }, () => {
      this.rehydratedFetch()
    })
  }

  onPerPageChange(perPage: number) {
    LocalStorage.set(LocalStorageKey.PER_PAGE, perPage)
    this.setState({ page: 1, perPage: perPage }, () => {
      this.rehydratedFetch()
    })
  }

  onSelfBillingOrderColumnsChange(columns: string[]) {
    LocalStorage.set(LocalStorageKey.INCOMING_SELF_BILLING_ORDER_COLUMNS, columns)
    this.forceUpdate()
  }

  render() {
    const { t } = this.props
    const { currentUser: { workspace: { setting } }, contactId } = this.props
    const { didInitialLoad, orders, totalPages, page, isFetching, activeFilters, sortValue, search, perPage } = this.state;

    const filtersActive = search?.length > 0 || Object.keys(activeFilters).length > 0
    const filters: IncomingSelfBillingOrderFilter[] = [
      { name: 'contact_id', label: t('IncomingSelfBillingOrdersTable::Client'), type: ResourceListFilterType.SINGLE_RESOURCE, resourceType: 'contact', isValidNewOption: () => false, visible: !Boolean(contactId) },
      { name: 'number', label: t('IncomingSelfBillingOrdersTable::Number'), type: ResourceListFilterType.STRING },
      { name: 'issue_date', label: t('IncomingSelfBillingOrdersTable::Issued on'), type: ResourceListFilterType.DATE },
      { name: 'due_date', label: t('IncomingSelfBillingOrdersTable::Due on'), type: ResourceListFilterType.DATE },
      { name: 'remittance_information', label: t('IncomingSelfBillingOrdersTable::Remittance information'), type: ResourceListFilterType.STRING },
      { name: 'amount', label: t('IncomingSelfBillingOrdersTable::Amount'), type: ResourceListFilterType.NUMBER },
      { name: 'booked_on', label: t('IncomingSelfBillingOrdersTable::Booked on'), type: ResourceListFilterType.DATE },
      { name: 'created_at', label: t('IncomingSelfBillingOrdersTable::Created date'), type: ResourceListFilterType.DATE },
    ]

    const { headers, columns, selectedColumns, renderRow } = getSelfBillingOrderTableRenderer({
      setting: setting,
      onRowClick: this.onTableClick,
      onActionClick: this.onTableActionClick,
    })

    return <>
      <NavigationDetection onRouteChange={this.onRouteChange} />
      <Helmet>
        <title>{t('IncomingSelfBillingOrdersTable::{{__appName}} | Incoming self-billing orders')}</title>
      </Helmet>
      {!didInitialLoad && <PageLoader />}
      {didInitialLoad && <ResourceTable
        data={orders}
        headers={headers}
        renderRow={renderRow}
        renderEmpty={<CardEmptyInfo
          icon={filtersActive ? 'search' : 'invoice'}
          description={filtersActive ? t('IncomingSelfBillingOrdersTable::No self billed orders found') : t('IncomingSelfBillingOrdersTable::No orders have been created yet')}
          descriptionActionText={filtersActive ? t('IncomingSelfBillingOrdersTable::Clear filters') : ""}
          onDescriptionActionClick={filtersActive ? this.onClearFilters : undefined}
        />}
        filters={filters}
        activeFilters={activeFilters}
        onFiltersChange={this.onFiltersChange}
        sortOptions={[
          { label: t('IncomingSelfBillingOrdersTable::Number ↑'), value: 'number_asc' },
          { label: t('IncomingSelfBillingOrdersTable::Number ↓'), value: 'number_desc' },
          { label: t('IncomingSelfBillingOrdersTable::Issued on ↑'), value: 'issue_date_asc' },
          { label: t('IncomingSelfBillingOrdersTable::Issued on ↓'), value: 'issue_date_desc' },
          { label: t('IncomingSelfBillingOrdersTable::Due on ↑'), value: 'due_date_asc' },
          { label: t('IncomingSelfBillingOrdersTable::Due on ↓'), value: 'due_date_desc' },
          { label: t('IncomingSelfBillingOrdersTable::Amount ↑'), value: 'amount_asc' },
          { label: t('IncomingSelfBillingOrdersTable::Amount ↓'), value: 'amount_desc' },
          { label: t('IncomingSelfBillingOrdersTable::Created at ↑'), value: 'created_at_asc' },
          { label: t('IncomingSelfBillingOrdersTable::Created at ↓'), value: 'created_at_desc' },
        ]}
        sortValue={sortValue}
        onSortChange={this.onSortValueChange}
        pagination={{ page: page, perPage: perPage, pageCount: totalPages }}
        onPageChange={this.onPageChange}
        onPerPageChange={this.onPerPageChange}
        isLoading={isFetching}
        searchValue={search}
        onSearchChange={this.onSearchChange}
        onSearchSubmit={this.onSearchSubmit}
        columns={columns}
        selectedColumns={selectedColumns}
        onColumnsChange={this.onSelfBillingOrderColumnsChange}
        maxHeight='65vh'
        stickyHeader={true}
      />}
    </>
  }
}

const mapStateToProps = (state: AppState): IStateToProps => {
  const {
    authentication: {
      currentUser,
    }
  } = state

  return {
    currentUser: currentUser,
  }
}

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): IDispatchToProps => {
  return {
    showConfirmModal: (options) => dispatch(showConfirmModal(options)),
    showIncomingSelfBillingOrderModal: (options) => dispatch(showIncomingSelfBillingOrderModal(options)),
  }
}

// @ts-ignore
export default withRouter(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(IncomingSelfBillingOrdersTable)) as any) as any